import { useEffect, useRef, useState } from "react";
import type { DocumentStyleKind } from "@/document/model/documentStyles";
import { DraggablePanel, stackedPanelInitialOffset } from "./DraggablePanel";
import { documentStyleKindLabel } from "./stylePreview";

export function CreateStyleDialog({
  kind,
  onConfirm,
  onCancel,
}: {
  kind: DocumentStyleKind;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onCancel();
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [onCancel]);

  function confirm(): void {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    onConfirm(trimmed);
  }

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey={`create-style-${kind}`}
      title={`New ${documentStyleKindLabel(kind).toLowerCase()} style`}
      widthClass="w-72"
      initialOffset={stackedPanelInitialOffset}
      onClose={onCancel}
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-[11px] uppercase text-slate-500">Name</span>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                confirm();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
              }
            }}
            className="h-8 w-full rounded border border-white/10 bg-black/20 px-2 text-[12px] text-slate-100 outline-none focus:border-white/20"
            placeholder="Style name"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded px-2.5 py-1.5 text-[11px] text-slate-400 hover:bg-white/[0.05] hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!name.trim()}
            className="rounded bg-sky-600 px-2.5 py-1.5 text-[11px] text-white hover:bg-sky-500 disabled:opacity-40"
          >
            Confirm
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
}
